// i18n foundation: react-i18next with namespace-per-domain bundles.
// English is the default and fallback; Spanish bundles live in client/src/locales/es.
// The active language is driven by the authenticated user's `language` preference
// (synced in App.tsx via LanguageSync) and cached in localStorage for instant boot.
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import enCommon from "./locales/en/common.json";
import esCommon from "./locales/es/common.json";
import enNav from "./locales/en/nav.json";
import esNav from "./locales/es/nav.json";
import enDashboard from "./locales/en/dashboard.json";
import esDashboard from "./locales/es/dashboard.json";
import enAccount from "./locales/en/account.json";
import esAccount from "./locales/es/account.json";
import enFloorplan from "./locales/en/floorplan.json";
import esFloorplan from "./locales/es/floorplan.json";
import enInventory from "./locales/en/inventory.json";
import esInventory from "./locales/es/inventory.json";
import enBank from "./locales/en/bank.json";
import esBank from "./locales/es/bank.json";
import enFinancial from "./locales/en/financial.json";
import esFinancial from "./locales/es/financial.json";
import enDocuments from "./locales/en/documents.json";
import esDocuments from "./locales/es/documents.json";
import enDean from "./locales/en/dean.json";
import esDean from "./locales/es/dean.json";
import enPages from "./locales/en/pages.json";
import esPages from "./locales/es/pages.json";
import enOperations from "./locales/en/operations.json";
import esOperations from "./locales/es/operations.json";
import enOnboarding from "./locales/en/onboarding.json";
import esOnboarding from "./locales/es/onboarding.json";
import enMarketplace from "./locales/en/marketplace.json";
import esMarketplace from "./locales/es/marketplace.json";
import enSocial from "./locales/en/social.json";
import esSocial from "./locales/es/social.json";
import enAuth from "./locales/en/auth.json";
import esAuth from "./locales/es/auth.json";
import enTax from "./locales/en/tax.json";
import esTax from "./locales/es/tax.json";
import enSavings from "./locales/en/savings.json";
import esSavings from "./locales/es/savings.json";

export type AppLanguage = "en" | "es";

const LANGUAGE_STORAGE_KEY = "dealermind_language";

export function getStoredLanguage(): AppLanguage {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored === "es" ? "es" : "en";
  } catch {
    return "en";
  }
}

export function setStoredLanguage(lang: AppLanguage) {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {}
}

i18n.use(initReactI18next).init({
  resources: {
    en: {
      common: enCommon,
      nav: enNav,
      dashboard: enDashboard,
      account: enAccount,
      floorplan: enFloorplan,
      inventory: enInventory,
      bank: enBank,
      financial: enFinancial,
      documents: enDocuments,
      dean: enDean,
      pages: enPages,
      operations: enOperations,
      onboarding: enOnboarding,
      marketplace: enMarketplace,
      social: enSocial,
      auth: enAuth,
      tax: enTax,
      savings: enSavings,
    },
    es: {
      common: esCommon,
      nav: esNav,
      dashboard: esDashboard,
      account: esAccount,
      floorplan: esFloorplan,
      inventory: esInventory,
      bank: esBank,
      financial: esFinancial,
      documents: esDocuments,
      dean: esDean,
      pages: esPages,
      operations: esOperations,
      onboarding: esOnboarding,
      marketplace: esMarketplace,
      social: esSocial,
      auth: esAuth,
      tax: esTax,
      savings: esSavings,
    },
  },
  lng: getStoredLanguage(),
  fallbackLng: "en",
  defaultNS: "common",
  interpolation: { escapeValue: false },
  returnEmptyString: false,
});

export default i18n;
